import React, { useEffect, useState } from "react";
import { supabase } from "../../lib/supabaseClient";

export default function UploadNewsletterModal({
  isOpen,
  onClose,
  onUploaded
}) {
  const [galleries, setGalleries] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  // --------------------------------------------------
  // Load galleries when modal opens
  // --------------------------------------------------
  useEffect(() => {
    if (!isOpen) return;

    async function loadGalleries() {
      const { data, error } = await supabase
        .from("Galeries")
        .select("id, nom")
        .order("nom");

      if (!error) setGalleries(data || []);
    }

    loadGalleries();
  }, [isOpen]);

  // --------------------------------------------------
  // Upload handler
  // --------------------------------------------------
  async function handleUpload() {
    if (!selectedGallery || !file) {
      alert("Sélectionne une galerie et un fichier PDF.");
      return;
    }

    try {
      setLoading(true);

      // 1) User
      const {
        data: { user }
      } = await supabase.auth.getUser();

      // 2) Upload file to storage
      const fileExt = file.name.split(".").pop();
      const filePath = `${selectedGallery.id}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("gallery-newsletters")
        .upload(filePath, file, {
          cacheControl: "3600",
          upsert: false,
          contentType: file.type
        });

      if (uploadError) throw uploadError;

      // 3) Insert contribution
      const { error: dbError } = await supabase
        .from("GalerieContributions")
        .insert([
 {
      gallery_id: selectedGallery.id,
      contributor_id: user?.id ?? null,
      contribution_type: "newsletter_auto_extraction",
      data: { file_url: filePath },     
      status: "pending"
    }
        ]);

      if (dbError) throw dbError;

      // 4) Success
      setFile(null);
      setSelectedGallery(null);
      setSearch("");

      onUploaded?.();
    } catch (err) {
      console.error("Erreur upload newsletter :", err);
      alert("Erreur lors de l'upload : " + err.message);
    } finally {
      setLoading(false);
    }
  }

  if (!isOpen) return null;

  // --------------------------------------------------
  // Render
  // --------------------------------------------------
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <h2 className="text-xl font-semibold mb-4">
          Importer une newsletter
        </h2>

        {/* Search gallery */}
        <input
          type="text"
          placeholder="Rechercher une galerie…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-2"
        />

        {/* Gallery list */}
        <div className="border rounded-lg max-h-40 overflow-y-auto mb-4">
          {galleries
            .filter((g) =>
              g.nom.toLowerCase().includes(search.toLowerCase())
            )
            .map((g) => (
              <div
                key={g.id}
                onClick={() => setSelectedGallery(g)}
                className={`px-3 py-2 cursor-pointer text-sm hover:bg-gray-100 ${
                  selectedGallery?.id === g.id
                    ? "bg-gray-100 font-medium"
                    : ""
                }`}
              >
                {g.nom}
              </div>
            ))}
        </div>

        {/* File */}
        <input
          type="file"
          accept="application/pdf"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="mb-4"
        />

        {/* Actions */}
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 rounded-lg border text-sm"
          >
            Annuler
          </button>
          <button
            onClick={handleUpload}
            disabled={loading}
            className="px-4 py-2 rounded-lg bg-black text-white text-sm disabled:opacity-50"
          >
            {loading ? "Upload…" : "Uploader"}
          </button>
        </div>
      </div>
    </div>
  );
}
